import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { BookOpen, Clock, Users, Play, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { coursesAPI, enrollmentsAPI } from '../lib/api';
import { toast } from 'sonner';

function CourseHeader({ course }) {
  const getDifficultyColor = (d) => {
    if (d === 'beginner') return 'bg-green-100 text-green-700';
    if (d === 'intermediate') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div>
      <div className="h-64 md:h-80 bg-gray-100 rounded-xl overflow-hidden border-2 border-black mb-6">
        {course.thumbnail_url ? (
          <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100">
            <BookOpen className="w-20 h-20 text-primary" />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge variant="outline" className="border-2 border-black">{course.category}</Badge>
        <Badge className={getDifficultyColor(course.difficulty)}>{course.difficulty}</Badge>
      </div>
      <h1 className="font-sketch text-4xl text-ink mb-4">{course.title}</h1>
      <div className="flex flex-wrap items-center gap-6 text-muted-foreground mb-6">
        <span className="flex items-center gap-2"><Clock className="w-5 h-5" />{course.duration_hours} hours</span>
        <span className="flex items-center gap-2"><Users className="w-5 h-5" />{course.enrolled_count} enrolled</span>
        <span className="flex items-center gap-2"><BookOpen className="w-5 h-5" />{course.modules?.length || 0} modules</span>
      </div>
      <p className="text-lg text-muted-foreground">{course.description}</p>
    </div>
  );
}

function EnrollCard({ enrollment, onEnroll, enrolling }) {
  if (enrollment) {
    return (
      <div className="card-sketchy text-center" data-testid="enrollment-card">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="font-sketch text-2xl text-ink">You are Enrolled!</h3>
        <p className="text-muted-foreground mt-2">Progress: {Math.round(enrollment.progress)}%</p>
        <Link to={`/learn/${enrollment.id}`}>
          <Button className="btn-sketchy w-full mt-4" data-testid="continue-learning-btn">
            <Play className="w-5 h-5 mr-2" /> Continue Learning
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="card-sketchy" data-testid="enrollment-card">
      <h3 className="font-sketch text-2xl text-ink mb-4">Start Learning</h3>
      <p className="text-muted-foreground mb-6">Enroll now and begin your learning journey.</p>
      <Button className="btn-sketchy w-full" onClick={onEnroll} disabled={enrolling} data-testid="enroll-btn">
        {enrolling ? 'Enrolling...' : 'Enroll Now - Free'}
      </Button>
    </div>
  );
}

function ModuleList({ modules, completedModules }) {
  if (!modules || modules.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>No modules yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {modules.map((module, index) => {
        const isCompleted = completedModules?.includes(module.id);
        return (
          <div key={module.id} className={`flex items-center gap-4 p-4 border-2 border-black rounded-lg ${isCompleted ? 'bg-green-50' : ''}`}>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center border-2 border-black font-bold">{index + 1}</div>
            <div className="flex-1">
              <h4 className="font-bold text-ink">{module.title}</h4>
              <p className="text-sm text-muted-foreground">{module.description}</p>
            </div>
            {isCompleted && <CheckCircle className="w-6 h-6 text-green-500" />}
          </div>
        );
      })}
    </div>
  );
}

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const courseRes = await coursesAPI.getById(courseId);
        setCourse(courseRes.data);
        
        const enrollmentsRes = await enrollmentsAPI.getMy();
        const userEnrollment = enrollmentsRes.data.find((e) => e.course_id === courseId);
        setEnrollment(userEnrollment);
      } catch (error) {
        toast.error('Failed to load course');
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, navigate]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const response = await enrollmentsAPI.enroll(courseId);
      setEnrollment(response.data);
      toast.success('Successfully enrolled!');
      navigate(`/learn/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!course) return null;

  return (
    <Layout>
      <div className="space-y-8" data-testid="course-detail">
        <Link to="/courses" className="inline-flex items-center gap-2 text-muted-foreground hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> Back to Catalog
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <CourseHeader course={course} />
          </div>
          <div className="lg:col-span-1">
            <EnrollCard enrollment={enrollment} onEnroll={handleEnroll} enrolling={enrolling} />
          </div>
        </div>

        <div className="mt-8">
          <h2 className="font-sketch text-2xl text-ink mb-6">Course Modules</h2>
          <ModuleList modules={course.modules} completedModules={enrollment?.completed_modules} />
        </div>
      </div>
    </Layout>
  );
}
